<template>
  <div class="c-search-on-click relative">
    <CButton
      v-if="inputFocused || searchValue"
      class="c-search-on-click__button absolute top-1 left-1 z-5 pa-2 box-border"
      :disabled="disabled"
      size="small"
      type="primary"
      @click.prevent="
        () => {
          if (!searchValue) inputFocused = false
          triggerSearch()
        }
      "
    >
      <template #icon> <CIcon icon="search" /> </template>
    </CButton>
    <NInputGroup>
      <CInput
        v-model:value="searchValue"
        :class="{ 'max-w-[320px] min-w-[280px]': !fullWidth, 'w-full': fullWidth, 'pl-8': inputFocused || searchValue }"
        :clearable="!!searchValue"
        :disabled="disabled"
        :on-clear="clear"
        :placeholder="placeholder"
        :style="{
          '--n-color': 'transparent',
          '--n-color-focus': 'transparent',
        }"
        @blur="handleBlur"
        @focus="inputFocused = true"
        @keyup.enter="triggerSearch"
      >
        <template #clear-icon>
          <CIcon class="relative -right-1" faType="fas" icon="circle-xmark" size="24px" />
        </template>
      </CInput>
      <slot name="options" />
    </NInputGroup>
  </div>
</template>

<script setup lang="ts">
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import { NInputGroup } from 'naive-ui'
import { nextTick, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    placeholder?: string
    fullWidth?: boolean
    initialSearchValue?: string
  }>(),
  { initialSearchValue: '' },
)

defineExpose({
  clear,
})

const emit = defineEmits<{
  search: [s: string]
}>()

const inputFocused = ref(false)
const searchValue = ref(props.initialSearchValue)

async function clear() {
  if (searchValue.value.length === 0) return

  setSearchValue('')
  await nextTick()
  triggerSearch()
}

function handleBlur(e: FocusEvent) {
  const relatedElement = e.relatedTarget as HTMLElement | null
  // only way to catch click on icon button while focusing out
  if (relatedElement?.classList?.contains('c-search-on-click__button')) return
  inputFocused.value = false
}

function setSearchValue(value: string) {
  searchValue.value = value
}

function triggerSearch() {
  emit('search', searchValue.value)
}
</script>
